/***
 *
 *   DATA SPECS
 *   View Data Specs
 *
 **********/

import React, {Fragment, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  AppLayoutContext, AuthContext, Button,
  DataSpecNumberCell,
  DateCell,
  Loader,
  ReactTable,
  useAPI,
  ViewContext
} from 'components/lib';
import axios from "axios";
import Style from "../../components/hero/hero.tailwind";
import {CreateDataSpecModal} from "./shared/CreateDataSpecModal";

export function DataSpecs(props){

  // inactive specs are hidden by default — they can't be used on new orders,
  // but stay available to search, duplicate and reactivate
  // fetched once including inactive specs — they're split into their own
  // table below, so activating/deactivating never needs a refetch
  const [dataSpecs, setDataSpecs] = useAPI('/api/dataSpec?includeInactive=true', 'get', true);
  const customers = useAPI('/api/customer/');
  const users = useAPI('/api/user/');
  const transponderModels = useAPI('/api/transponderModels/');
  const context = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const isAdmin = useAPI('/api/adminPanel/admin/' + context.user?.user_id);

  let navigate = useNavigate();
  const [openDataSpecModal, setOpenDataSpecModal] = useState(false);
  const [duplicateOrCreateToggle, setDuplicateOrCreateToggle] = useState(false);
  const [createGlobalDataSpec, setCreateGlobalDataSpec] = useState(false);
  // Add buttons to nav header (needed even if empty, to clear stale buttons from prev pages)
  const [navButtons, setNavButtons] = useContext(AppLayoutContext);
  useEffect(() => {
    setNavButtons([]);
  }, []);



  /*
  * Toggle a spec straight from the list: Active -> Inactive and back.
  * Inactive specs can't be linked to new orders.
  */
  const toggleSpecActive = React.useCallback(async (spec) => {

    const nextActive = spec.is_active === false;

    try {

      await axios.patch(`/api/dataSpec/${spec.id}/active`, { is_active: nextActive });

      // flip it locally; the visible list filter takes care of hiding it
      setDataSpecs(prev => ({
        ...prev,
        data: prev.data.map(d => d.id === spec.id ? { ...d, is_active: nextActive } : d)
      }));

      viewContext.notification.show(
          nextActive
              ? `${spec.number?.split('_')[0]} reactivated.`
              : `${spec.number?.split('_')[0]} deactivated — it can no longer be added to new orders.`,
          'success',
          true
      );

    } catch (error) {

      console.error('Failed to change data spec status:', error);
      viewContext.notification.show('Could not change the Data Spec status.', 'error', true);
    }

  }, [setDataSpecs, viewContext]);

  // active specs are listed on their own; inactive ones get their own table below
  const activeDataSpecs = React.useMemo(
      () => dataSpecs.data ? dataSpecs.data.filter(d => d.is_active !== false) : [],
      [dataSpecs.data]
  );

  const inactiveDataSpecs = React.useMemo(
      () => dataSpecs.data ? dataSpecs.data.filter(d => d.is_active === false) : [],
      [dataSpecs.data]
  );

  const columns = React.useMemo(() => [
    {
      Header: "DS Number",
      //accessor: 'number',
      accessor: (row) => {
        return row.number.split('_')[0]
      },
      Cell: DataSpecNumberCell,
    },
    {
      Header: "Organization",
      id: "customer_id",
      accessor: (row) => {
        return row && row.customer_id && customers.data ? customers.data.find(u => u.id === row.customer_id).name: row.customer_id
      },
    },
    {
      Header: "Description",
      accessor: (row) => {
        return row.description && row.description.length > 130 ? row.description.slice(0,130) + '...': row.description;
      },
    },
    {
      Header: "Printing",
      accessor: (row) => {
        if (!row.printConfig) {
          return '-'
        }
        if (row.printConfig.printRecords.length <= 0) {
          return 'No records'
        }
        return `${row.printConfig.printRecords.length} ${row.printConfig.printRecords.length === 1 ? 'Record' : 'Records'}`;
      },
    },
    {
      Header: "Encoding",
      accessor: (row) => {
        if (!row.transponderConfigs || row.transponderConfigs.length <= 0) {
          return '-'
        }
        if (row.transponderConfigs.flatMap(tc => tc.dataRecords).length <= 0) {
          return `${row.transponderConfigs.length} ${row.transponderConfigs.length === 1 ? 'Chip' : 'Chips'}, No records`
        }
        return `${row.transponderConfigs.length} ${row.transponderConfigs.length === 1 ? 'Chip' : 'Chips'}, ${row.transponderConfigs.flatMap(tc => tc.dataRecords).length} ${row.transponderConfigs.flatMap(tc => tc.dataRecords).length === 1 ? 'Record' : 'Records'}`;
      },
    },
    {
      Header: "Chip(s)",
      accessor: (row) => {
        if (!row.transponderConfigs || row.transponderConfigs.length === 0) {
          return "-";
        }

        return row.transponderConfigs
            .map((tc) => {
              const model = transponderModels?.data?.find(
                  (u) => u.id === tc.transponder_model_id
              );
              return model ? model.name : "Unknown";
            })
            .join(", ");
      },
    },
    {
      Header: "Assigned To",
      accessor: (row) => {
        return row && row.assigned_to_id && users.data ? users.data.find(u => u.id === row.assigned_to_id).name: row.assigned_to_id
      },
    },
    {
      Header: "Created",
      accessor: 'created_at',
      Cell: DateCell,
    },
    {
      Header: "Created By",
      accessor: (row) => {
        return row && row.created_by_id && users.data ? users.data.find(u => u.id === row.created_by_id).name: row.created_by_id
      },
    },
    {
      Header: "Status",
      id: "is_active",
      accessor: (row) => row.is_active === false ? 'Inactive' : 'Active',
      Cell: ({ row, value }) => (
          <span
              className={"cursor-pointer hover:underline text-sm text-orange-500"}
              title={value === 'Active' ? 'Click to deactivate' : 'Click to reactivate'}
              onClick={() => toggleSpecActive(row.original)}
          >
            {value}
          </span>
      ),
    },
  ], [users, customers, toggleSpecActive])

  return (
    <Fragment>

      { dataSpecs.data == null || dataSpecs.loading || users.loading || customers.loading || transponderModels.loading ?
        <Loader /> :
        <>
          {/*<Button goto='/dataspec/new' text='New Data Spec' small className={ Style.button + " m-2 mb-3 bg-orange-500 hover:bg-orange-700"}/>*/}
          <div className={"flex"}>
            <Button action={() => {
              setOpenDataSpecModal(true);
              setCreateGlobalDataSpec(false);
              setDuplicateOrCreateToggle(false);
            }} text='New Data Spec' small className={ Style.button + " m-2 mb-3 bg-orange-500 hover:bg-orange-700"}/>
            <Button action={() => {
              setDuplicateOrCreateToggle(true);
              setCreateGlobalDataSpec(false);
              setOpenDataSpecModal(true);
            }} text='Duplicate Existing Dataspec' small className={ Style.button + " m-2 mb-3 bg-orange-500 hover:bg-orange-700"}/>

            {isAdmin.data.isAdmin ?
            <Button action={() => {
              setOpenDataSpecModal(true);
              setCreateGlobalDataSpec(true);
              setDuplicateOrCreateToggle(false);
            }} text='Create Global Dataspec' small className={ Style.button + " m-2 mb-3 bg-orange-500 hover:bg-orange-700"}/>
                : ''}

          </div>
          <ReactTable columns={columns} data={activeDataSpecs} storageKey="dataSpecs.pageSize" />

          { inactiveDataSpecs.length > 0 &&
              <>
                <h3 className={"text-xl font-semibold mb-4 mt-8 ml-2 leading-6 text-gray-900"}>
                  Inactive Data Specifications
                  <span className={"ml-2 text-gray-400 text-sm font-normal"}>
                    cannot be linked to new orders — click the status to reactivate
                  </span>
                </h3>
                <ReactTable columns={columns} data={inactiveDataSpecs} storageKey="dataSpecs.inactive.pageSize" />
              </>
          }
          <CreateDataSpecModal open={openDataSpecModal}
                     setOpen={setOpenDataSpecModal}
                     afterCreate={(data) => {
                       navigate(`/dataSpec/${data.id}`);
                     }}
                     is_duplicate={duplicateOrCreateToggle}
                     createGlobalDataSpec={createGlobalDataSpec}
          />
        </>
      }
    </Fragment>
  )
}
